import {
  AppstoreOutlined,
  EditOutlined,
  GlobalOutlined,
  GoogleOutlined,
  MailFilled,
} from '@ant-design/icons';
import { AccordionItem, Button, ThreeDotMenu, Tooltip } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import { QuestionCircleIcon } from '@mm/icons';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import {
  EmailAccount,
  NewEmailAccount,
} from '@mm/state/interfaces/emailAccount';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { selectEmailAccounts } from '@mm/state/selectors/emailAccountsSelectors';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import classNames from 'classnames';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { ReactComponent as AccountIcon } from '../../../../../../../../layout/BotBuilderSidebar/UserDropdown/UserDropdownIcons/UserFallback.svg';
import { EmailAccountEditor } from './EmailAccountEditor';

const isGmail = (emailAddress?: string) => {
  return emailAddress?.includes('gmail.com');
};

const EmailAccountIcon = ({ kind }) => {
  switch (kind) {
    case 'gmail':
      return (
        <>
          {' '}
          <GoogleOutlined /> Gmail
        </>
      );
    case 'microsoft':
      return (
        <>
          <AppstoreOutlined /> Microsoft
        </>
      );
    default:
      return (
        <>
          {' '}
          <GlobalOutlined /> Manual
        </>
      );
  }
};

export const EmailIntegration: FunctionComponent = () => {
  const { getAccounts, disconnect } = useEmailAccountCRUDActions();
  const { retrievePage: retrieveFacebookPage } = useConnectedPageCRUDActions();
  const { getGmailOauthUrl } = useEmailAccountCRUDActions();
  const { openGeneralConfirmModal } = useModalActions();
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const accounts = useSelector(selectEmailAccounts);
  const [emailAccountUnderEdit, setEmailAccountUnderEdit] = useState<
    NewEmailAccount | EmailAccount
  >();
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;

  const { openActivateEmailAccountModal } = useModalActions();
  const { openUniversalUpgradeLandmineModal } = useModalActions();

  const reauthorizeAccount = async (account: EmailAccount) => {
    if (account.kind !== 'gmail' || !account.credential?.email) {
      return;
    }
    window.location.href = `${await getGmailOauthUrl(
      facebookPageId,
    )}&login_hint=${account.credential.email}`;
  };
  const onConnectAccountClick = () => {
    if (!hasNativeIntegrationsFeature) {
      openUniversalUpgradeLandmineModal({
        landmineName: landmineNames.INTEGRATIONS_NATIVE_INTEGRATIONS,
        feature: 'nativeIntegrations',
        onSuccessBilling: openActivateEmailAccountModal,
      });
    } else {
      openActivateEmailAccountModal();
    }
  };

  const onDisconnect = (account: EmailAccount) => {
    openGeneralConfirmModal({
      headerText: 'Confirm Account Disconnection',
      messageText: (
        <>
          <div>
            Once disconnected, all existing conversations via this email account
            will be terminated.
          </div>

          <div>
            <strong>Disconnected accounts cannot be restored.</strong>
          </div>
        </>
      ),
      onCloseHandler: async (ok: boolean) => {
        if (ok) {
          await disconnect(account);
          retrieveFacebookPage(facebookPageId);
        }
      },
      okButtonText: 'Disconnect',
      cancelButtonFirst: true,
      okButtonStyling: {
        red: true,
      },
      css: {
        primaryButton: 'integrations__diconnect-modal-primary-button',
        secondaryButton: 'integrations__confirm-modal-secondary-button',
      },
    });
  };
  useEffect(() => {
    getAccounts(facebookPageId);
  }, [facebookPageId, getAccounts]);

  const elements = [{ name: 'Disconnect', value: 'disconnect' }];

  const onMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const header = (
    <div
      className={classNames('integrations__header', {
        'integrations__header--account-connected': accounts.length,
      })}
    >
      <div className="integrations__header-main">
        <div className="integrations__header-icon integrations__email-header-icon">
          <MailFilled className="integrations__email-filled-icon" />
        </div>
        <div>
          <div className="integrations__header-title">Email Integration</div>
          <div className="integrations__header-info">
            <div className="integrations__header-learn-more">
              Connect automations to your email account
            </div>
          </div>
        </div>
      </div>

      <div className="integrations__header-actions">
        {!!accounts.length && (
          <div className="integrations__header-status">
            Email Accounts
            <AccountIcon className="integrations__header-status-icon" />
            <div className="integrations__header-status-count">
              {accounts.length}
            </div>
          </div>
        )}
        <PrimaryButton
          className={classNames(
            accounts.length
              ? 'integrations__header-add-button-secondary'
              : 'integrations__header-add-button',
            'integrations__header-button-no-margin',
          )}
          onClick={e => {
            e?.stopPropagation();
            onConnectAccountClick();
          }}
          color={
            !accounts.length ? ButtonColor.blue : ButtonColor.secondaryBlue
          }
        >
          Add an Account
        </PrimaryButton>
      </div>
    </div>
  );

  const editor = (
    <EmailAccountEditor
      emailAccountUnderEdit={emailAccountUnderEdit}
      onClose={() => setEmailAccountUnderEdit(undefined)}
    />
  );

  const modalActions = useModalActions();
  const onIncreaseLimitClick = () => {
    modalActions.openIncreaseEmailLimitModal({
      onSubmit: onConnectAccountClick,
    });
  };

  const renderLimited = (emailAddress?: string) => {
    if (isGmail(emailAddress)) {
      return (
        <div
          className="integrations-item__limit-notice"
          onClick={onIncreaseLimitClick}
        >
          <span>Sending is limited</span>
          <span>Connect more</span>
        </div>
      );
    }

    return <></>;
  };

  if (!accounts.length) {
    return (
      <div className="integrations-component">
        {editor}
        {header}
      </div>
    );
  }

  return (
    <div className="integrations-component">
      {editor}
      <AccordionItem
        className="integrations-item"
        caretStyleIcon
        closeOnOutsideClick={false}
        openOnMount={!!accounts.length}
        titleComponent={header}
      >
        {!!accounts.length && (
          <div className="integrations-item__your-accounts">
            <table className="integrations-item__table">
              <thead>
                <th>Account Type</th>
                <th>Sender Name</th>
                <th>Email Address</th>
                <th>Actions</th>
              </thead>
              <tbody>
                {accounts.map(account => (
                  <tr key={account.id}>
                    <td>
                      <EmailAccountIcon kind={account.kind} />
                    </td>
                    <td>{account.name}</td>
                    <td>
                      <div className="integrations-item__email-address">
                        {account.emailAddress}{' '}
                        {renderLimited(account.emailAddress)}
                        {account.kind === 'gmail' &&
                          account.credential &&
                          !account.credential.fullGmailScope && (
                            <>
                              <Button
                                color="blue"
                                size="sm"
                                onClick={() => reauthorizeAccount(account)}
                              >
                                Reauthorize Account
                              </Button>
                              <Tooltip anchorElement={<QuestionCircleIcon />}>
                                Reauthorizing your account will allow us to send
                                email using a more secure method, ensuring more
                                reliable message delivery
                              </Tooltip>
                            </>
                          )}
                      </div>
                    </td>

                    <td className="integrations__three-dot-menu-wrapper">
                      <EditOutlined
                        className="integrations__edit-icon"
                        onClick={() => setEmailAccountUnderEdit(account)}
                      />
                      <ThreeDotMenu
                        className="integrations__three-dot-menu"
                        elements={elements}
                        selectItem={value =>
                          value === 'disconnect' && onDisconnect(account)
                        }
                        onClick={onMenuClick}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AccordionItem>
    </div>
  );
};
